import { Box, Text } from '@chakra-ui/react';

import {
  DynamicPriceDescription,
  DynamicPriceTitle,
} from '~/features/accreditation/components/dynamic-price-top-bar-item';
import { WoolfProgram } from '~/features/accreditation/components/woolf-program-card';
import mbaElectiveKeys from '~/features/accreditation/constants/approved-elective-keys-mba.json';
import { accreditationNewsItems } from '~/features/accreditation/constants/news-items';
import { AccreditationPageContentProps } from '~/features/accreditation/types';
import {
  BookIcon,
  CircleArrowRightIcon,
  CircleCheckIcon,
  ClockIcon,
  DegreeIcon,
  RocketIcon,
  SchoolIcon,
  TagIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';

const ICON_SIZE = { base: '18px', md: '24px' };
const TOPBAR_ICON_SIZE = { base: '18px', lg: '24px' };

const mbaWoolfToUdacityProgramKeys: Record<string, string[]> = {
  'woolf-growth-product-management': ['nd037'],
  'woolf-digital-marketing': ['nd018'],
  'woolf-implementation-and-product-introduction': ['nd036', 'nd088'],
  'woolf-business-leadership-and-strategy': ['nd054', 'nd055'],
  'woolf-statistics-and-data-analysis': ['nd429', 'ud201', 'st101'],
};

const mbaCorePrograms: WoolfProgram[] = [
  {
    key: 'woolf-implementation-and-product-introduction',
    title: 'Implementation and Product Introduction',
    image: 'https://video.udacity-data.com/topher/2024/October/67098755_nd036/nd036.jpg',
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '250 hours',
  },
  {
    key: 'woolf-business-leadership-and-strategy',
    title: 'Business Leadership and Strategy',
    image: 'https://video.udacity-data.com/topher/2024/October/67098763_nd054/nd054.jpg',
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '75 hours',
  },
  {
    key: 'woolf-statistics-and-data-analysis',
    title: 'Statistics and Data Analysis',
    image: 'https://video.udacity-data.com/topher/2024/October/670987b4_nd429/nd429.jpg',
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '75 hours',
  },
  {
    key: 'woolf-growth-product-management',
    title: 'Growth Product Management',
    image: 'https://video.udacity-data.com/topher/2024/October/67098758_nd037/nd037.jpg',
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '100 hours',
  },
  {
    key: 'woolf-digital-marketing',
    title: 'Digital Marketing',
    image: 'https://video.udacity-data.com/topher/2024/October/67098737_nd018/nd018.jpg',
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
];

export const mbaAiContent: AccreditationPageContentProps = {
  colorScheme: 'crimson',
  formalDegreeKey: 'mba_ai',

  hero: {
    title: 'The MBA designed for\nAI Product Management',
    description:
      'An accredited MBA that blends business fundamentals with AI product leadership through hands-on learning and projects you can use at your job.',
    brochureUri: 'https://cdn.sanity.io/files/tlr8oxjg/production/d616b2e5c823c6dc94e3595aada5b5cad03aea6a.pdf',
    backgroundImageOverlayGradient: {
      md: 'linear-gradient(to right, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.2) 65%, rgba(0, 0, 0, 0.4) 85%, rgba(0, 0, 0, 0.8) 100%)',
      '2xl':
        'linear-gradient(to right, rgba(0, 0, 0, 0.65) 0%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.65) 100%)',
    },
    heroTextMaxWidth: { base: '100%', md: '700px' },
    backgroundImages: {
      mobile: '/images/accreditation/mba-hero-bg-mobile.png',
      desktop: '/images/accreditation/mba-hero-bg.png',
      objectPosition: {
        mobile: 'center -60px',
        desktop: 'center right',
      },
    },
  },

  topBarItems: [
    {
      id: 'price',
      icon: <TagIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: <DynamicPriceTitle fallback='Less than $5,000 USD*' formalDegreeKey='mba_ai' />,
      description: (
        <DynamicPriceDescription fallback='Only $199 + Udacity subscription to start.' formalDegreeKey='mba_ai' />
      ),
    },
    {
      id: 'duration',
      icon: <ClockIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: '18-24 months',
      description: 'Avg time to complete 2,250 study hours',
    },
    {
      id: 'capstone',
      icon: <BookIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: 'Capstone project',
      description: 'Graduate with a degree and portfolio',
    },
    {
      id: 'degree',
      icon: <DegreeIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: 'Accredited MBA',
      description: 'Degree awarded by Woolf',
    },
  ],

  values: {
    sectionTitle: 'An MBA designed for AI product builders',
    sectionDescription: (
      <Text color='grey.500' margin='0 auto' maxWidth='650px'>
        <Box as='span' color='grey.900' fontWeight='700'>
          The Udacity Institute of AI & Technology
        </Box>{' '}
        combines real-world projects with expert support, so you don&apos;t just learn, you advance.
      </Text>
    ),
    items: [
      {
        imageSrc: '/images/accreditation/value-icon--chart-2x.png',
        title: 'Modular journey to AI product mastery',
        description:
          'Build step by step, from foundational AI concepts to real product applications, on a path you can tailor to your goals.',
      },
      {
        imageSrc: '/images/accreditation/value-icon--compass-2x.png',
        title: 'Guidance from Mentors',
        description:
          'Get support from experienced mentors who help you stay on track, answer questions, and guide your progress.',
      },
      {
        imageSrc: '/images/accreditation/value-icon--circle-2x.png',
        title: 'Flexible and 100% online',
        description: 'Study anytime, anywhere. Designed to fit work and life without pausing your career.',
      },
      {
        imageSrc: '/images/accreditation/value-icon--rod-2x.png',
        title: 'Project-based learning',
        description: 'Graduate with portfolio-ready work that shows how you lead AI products end\u2011to\u2011end.',
      },
    ],
  },

  stats: {
    heading: 'Maximum career impact, lasting value',
    subheading:
      'An accredited MBA that builds job-ready confidence with a respected credential, a portfolio, and leadership skills.',
    stats: [
      {
        number: '1.75',
        suffixChar: 'x',
        descriptionTop: 'Higher starting salary',
        descriptionBottom: "for MBA vs. bachelor's graduates",
        suffix: 1,
      },
      {
        number: '56',
        descriptionTop: 'Wage premium',
        descriptionBottom: 'for workers with specialized AI skills',
        suffix: 2,
      },
      {
        number: '92',
        descriptionTop: 'More affordable',
        descriptionBottom: 'compared to a $62k avg USA program cost',
        suffix: 3,
      },
    ],
    sources: [
      {
        suffix: 1,
        label: 'GMAC Corporate Recruiters Survey 2024',
        href: 'https://www.gmac.com/-/media/files/gmac/research/employment-outlook/2024-corporate-recruiters-survey/2024_gmac_crs_regional_profile-united-states.pdf',
      },
      {
        suffix: 2,
        label: 'PwC 2025 Global AI Jobs Barometer',
        href: 'https://www.pwc.com/gx/en/issues/artificial-intelligence/ai-jobs-barometer.html',
      },
      {
        suffix: 3,
        label: "Average Cost of a Master's Degree (MBA Section)",
        href: 'https://educationdata.org/average-cost-of-a-masters-degree',
      },
    ],
  },

  pathTitle: 'Starting your MBA journey',
  pathDescription: 'From first step to graduation, it\u2019s simple, flexible, and built for working professionals.',
  pathSteps: [
    {
      icon: <RocketIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '1. Subscribe to Udacity',
      description: 'Become a subscriber to unlock the application process.',
    },
    {
      icon: <TagIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '2. Pay application fee',
      description: 'All applicants must pay a one-time non-refundable fee.',
    },
    {
      icon: <CircleCheckIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '3. Apply online',
      description: 'Submit a short application.',
    },
    {
      icon: <CircleArrowRightIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '4. Start learning',
      description: 'Access the full curriculum, projects, and mentorship.',
    },
    {
      icon: <SchoolIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '5. Earn credit',
      description: 'Earn credit for prior and current learning.',
    },
    {
      icon: <DegreeIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '6. Receive your MBA',
      description: 'Graduate and go forth with confidence in your capabilities!',
    },
  ],

  about: {
    certificateImage: '/images/accreditation/mba-ai-certificate.png',
    description: (
      <Text color='grey.500'>
        The Udacity Institute of AI and Technology, a member college of Woolf, offers a pathway to a Master in Business
        Administration. Students complete Udacity&apos;s hands-on, project-based curriculum while earning ECTS credits.
        Degrees are awarded by{' '}
        <Box as='a' color='crimson.500' href='https://woolf.university' target='_blank' textDecoration='underline'>
          Woolf
        </Box>
        , a global collegiate Higher Education Institution recognized in 60+ countries, ensuring the same academic
        recognition as other higher education institutions, at a fraction of the cost and considerably more flexible
        than a traditional Master&apos;s program.
      </Text>
    ),
  },

  prerequisites: {
    degreeTitle: 'MBA',
    feeDescription: 'One-time application fee',
    prerequisites: [
      "Bachelor's degree (recommended) or equivalent experience.",
      'English proficiency (for coursework and assessments).',
      'Udacity monthly subscription (required for application).',
    ],
    stampImage: '/images/accreditation/stamp.png',
  },

  milestones: {
    sectionTitle: 'How you earn your MBA',
    electiveKeys: mbaElectiveKeys,
    electiveCreditHours: 875,
    capstoneCreditHours: 750,
    electiveDescription:
      'Choose from 13+ electives and complete 875 unique credit hours in the areas that interest you most.',
    coreDescription:
      'Build your foundation with these industry-relevant courses to complete 625 credit hours. These cover business leadership, product strategy, analytics, and AI capabilities.',
    capstoneDescription:
      'Graduate with a portfolio-quality project that demonstrates your ability to lead AI product initiatives from problem definition to measurable business impact. This accounts for 750 credit hours.',
    modalAlertText: 'The Udacity programs below need to be completed for this MBA course.',
    corePrograms: mbaCorePrograms,
    woolfToUdacityProgramKeys: mbaWoolfToUdacityProgramKeys,
  },

  resources: {
    videoQaCard: {
      heading: 'Everything you need to know in under 5 minutes',
      tag: 'MBA Focused in AI',
    },
    videos: {
      videoQA: {
        id: 'FtNTrrTAqNw',
        title: 'Everything you need to know in under 5 minutes',
      },
    },
    featuredVideoCard: {
      title: 'New standard for product managers',
      description: 'Build, lead, and grow products in the age of AI',
      portraitPath: '/images/accreditation/marty-cagan.png',
      href: '/video/new-standard-for-product-managers-replay',
      speakerLabel: 'with Marty Cagan',
      background: 'linear-gradient(-63.61deg, #540916 0%, #0A0B0F 100%)',
    },
  },

  testimonials: [
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/owen-ray.png',
      labelKey: 'testimonials.owenRay.label',
      quoteKey: 'testimonials.owenRay.quote',
      name: 'Owen Ray',
      title: 'Agentic AI for Business Leaders',
      quoteColor: 'light',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/daniel-pardhe.png',
      labelKey: 'testimonials.danielPardhe.label',
      quoteKey: 'testimonials.danielPardhe.quote',
      name: 'Daniel Pardhe',
      title: 'Growth Product Manager',
      quoteColor: 'light',
      labelColor: 'light',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/jordan-shephard.png',
      labelKey: 'testimonials.jordanShephard.label',
      quoteKey: 'testimonials.jordanShephard.quote',
      name: 'Jordan Shephard',
      title: 'Product Manager',
      quoteColor: 'dark',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/federico-martini.png',
      labelKey: 'testimonials.federicoMartini.label',
      quoteKey: 'testimonials.federicoMartini.quote',
      name: 'Federico Martini',
      title: 'AI Engineering Manager & Tech Leader',
      quoteColor: 'light',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/sabrina-palis.png',
      labelKey: 'testimonials.sabrinaPalis.label',
      quoteKey: 'testimonials.sabrinaPalis.quote',
      name: 'Sabrina Palis',
      title: 'AI & Data Consultant',
      quoteColor: 'light',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/gabbita-srinivasa.png',
      labelKey: 'testimonials.gabbitaSrinivasa.label',
      quoteKey: 'testimonials.gabbitaSrinivasa.quote',
      name: 'Gabbita Srinivasa',
      title: 'Introduction to Programming',
      quoteColor: 'dark',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/rey-rosales.png',
      labelKey: 'testimonials.reyRosales.label',
      quoteKey: 'testimonials.reyRosales.quote',
      name: 'Rey Rosales',
      title: 'Professor',
      quoteColor: 'light',
      labelColor: 'light',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/dmytro-kysylychyn.png',
      labelKey: 'testimonials.dmytroKysylychyn.label',
      quoteKey: 'testimonials.dmytroKysylychyn.quote',
      name: 'Dmytro Kysylychyn',
      title: 'Machine Learning DevOps Engineer',
      quoteColor: 'dark',
      labelColor: 'light',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/subramanya-nataraj.png',
      labelKey: 'testimonials.subramanyaNataraj.label',
      quoteKey: 'testimonials.subramanyaNataraj.quote',
      name: 'Subramanya Nataraj',
      title: 'Research Assistant',
      quoteColor: 'light',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/allan-bravos.png',
      labelKey: 'testimonials.allanDiegoBravos.label',
      quoteKey: 'testimonials.allanDiegoBravos.quote',
      name: 'Allan Diego Bravos',
      title: 'Head of Data & AI',
      quoteColor: 'light',
      labelColor: 'light',
    },
  ],

  faqs: [
    {
      question: 'What is the Udacity Institute of AI & Technology?',
      answer:
        'The Udacity Institute of AI & Technology is a member college of Woolf that provides learners with a path to a fully online MBA program. Woolf is an accredited higher education institution recognized in 60+ countries. The degree consists of 2,250 credit hours, or the equivalent of about 14 Udacity Nanodegrees and a capstone project. 7 Nanodegrees and 2 courses form the mandatory core modules, and students can earn the remainder of their credits through electives and the capstone project. This provides a structured pathway combining project-based, industry-aligned coursework with academic recognition.',
    },
    {
      question: 'Who can apply to this degree program?',
      answer:
        "Anyone with an undergraduate degree is eligible to apply. If you don't have a degree, you may still be eligible through the Performance-Based Admission (PBA) pathway.",
    },
    {
      question: 'Is GMAT required for enrolling into the MBA?',
      answer: 'No, there is no requirement of a GMAT score for applying for the MBA.',
    },
    {
      question: 'Can I apply the hours from past Udacity programs toward this degree?',
      answer:
        "If you've previously completed eligible Udacity coursework, your study hours may already count toward the degree. Sometimes this process may involve additional work or remedial work depending on the courses you've completed, but if you enroll in the degree program, you may well be able to convert those hours into official academic credit.",
    },
    {
      question: 'Are the credits I earn from this program transferrable to other colleges or universities?',
      answer:
        'Yes! Woolf is an accredited higher education institution recognized in 60+ countries. You can typically transfer credits to other institutions depending on their acceptance policies.',
    },
    {
      question: 'Am I required to pay the full program fee of $5000 USD?',
      answer:
        'No. Getting started simply requires you to pay the enrollment fee of $199 USD and be a Udacity subscriber which starts at $249 USD a month. The estimated cost of $5000 assumes it takes 19-months to finish the full program at $249/month. However, you can cancel at anytime.',
    },
    {
      question: "Can I enroll in two master's degree programs simultaneously?",
      answer:
        'Yes, you can be enrolled in two master\u2019s degree programs at the same time. You would have to separately pay the one-time enrollment fee and complete the application for each degree program. Having a Udacity subscription is a prerequisite for enrollment.',
    },
    {
      question: 'Do I get credit for the work I do if I cancel my subscription?',
      answer:
        'As you successfully complete each course in the MBA program, you earn corresponding academic credit. If you pause or cancel, you will still have an academic transcript with ECTS credits for your coursework that you can transfer to another program.',
    },
    {
      question: "Do I need to repeat a Udacity Nanodegree program for the MBA if I've previously taken it?",
      answer:
        "No. If you've previously graduated from a Nanodegree program that's included in the MBA, you will receive credit for that Nanodegree program and will not need to take it again. However, you will be required to complete the remediation exercise for that Nanodegree program which includes required academic reading and an assessment to ensure you understand the material.",
    },
    {
      question: 'Where is the Woolf degree from the Udacity Institute of AI and Technology recognized?',
      answer:
        'A Woolf degree is equivalent to a degree from any accredited European university, as Woolf uses the ECTS system. Woolf degrees are recognized through both the Lisbon Recognition Convention and individual country equivalencies in 60+ countries. Additionally, Woolf degrees are considered equivalent to those from accredited universities in the US, Canada, and more. For validation checks (e.g., for immigration), students can email support@woolf.education.',
    },
    {
      question: 'How long does it take to complete the degree?',
      answer:
        'The program is self-paced, so the time it takes depends on your schedule and learning pace. A learner who spends 25 hours a week on the program will complete it in just under 24 months. Most learners typically complete the degree in 18\u201324 months.',
    },
    {
      question:
        'How long does it take for my previous learning in Udacity to apply as credits towards the MBA program?',
      answer:
        "After you apply it can take up to five business days for the work you've previously completed in Udacity to be added to your transcript as academic credit.",
    },
    {
      question: 'Can I take a break and pause the program?',
      answer:
        "Yes. You can pause your subscription for up to 90 days at a time. During the pause, you are removed from the degree program, however, your progress is retained. Once you reactivate your subscription and resume payment, you'll be able to continue your learning. After resuming, you'll need to stay active for at least one month before pausing again.",
    },
    {
      question: 'Do I need to stay subscribed to Udacity to remain in the program?',
      answer:
        "Yes. As long as your Udacity subscription is active, you'll stay enrolled in the MBA program. If you cancel your subscription or request to unenroll, your participation in the degree program ends. However, your degree progress will be retained and if you re-enroll later, you can continue your degree program at that point.",
    },
    {
      question: 'What does the $199 enrollment fee cover?',
      answer:
        'The fee covers application processing, admission review and academic credit conversion. It helps support the systems that issue and manage your official academic credit.',
    },
    {
      question: 'Do I have to pay the fee again if I re-enroll later?',
      answer:
        'No, if you cancel your subscription and later decide to re-enroll, you are not required to pay the fee again. You can reach out to Udacity Support to reactivate your degree program, and you can continue from where you left off.',
    },
    {
      question: 'What is Woolf?',
      answer:
        "Woolf is a global collegiate higher education institution that lets qualified organizations join as new member colleges and offer accredited degrees. Woolf's mission is to increase access to world-class higher education and ensure that it is globally recognized and transferable. Woolf is composed of 29 member colleges, and degrees from Woolf are fully accredited in the ECTS system and recognized in 60+ countries.",
    },
  ],
  faqsSupportLink: 'https://support.udacity.com/hc/en-us/sections/44025723216909-Master-of-Business-Administration',

  footer: {
    heading: 'Ready to start your journey?',
    description:
      "One short application and a one-time $199 application fee. That's all it takes to start working towards your Master of Business Administration.",
    backgroundImages: {
      mobile: '/images/accreditation/mba-footer-bg-mobile.png',
      desktop: '/images/accreditation/mba-footer-bg.png',
    },
  },

  newsItems: accreditationNewsItems,
};
